import { queryActivityList, queryMerchantShopTabList } from "@/api/activity";
import { queryBonusByActId } from "@/api/year-end-bonus";
import { useActions } from "@/utils/actions";
import { getUserRole } from "@/utils/identity";
import { hostEnv } from "@/utils/platform";
import Track from "@/utils/track";
import useNavigation from "@/utils/useNavigate";
import { ActivityCardNormalV2Comp, ShopCardV2 } from "@akc/mx-taro-h5-components";
import { InfiniteScroll, PullToRefresh } from "antd-mobile";
import { DownOutline, UpOutline } from "antd-mobile-icons";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import OverviewPane from "./components/OverviewPane";
import RulePageIcon from "./components/RulePageIcon";
import SubProcessItem from "./components/SubProcessItem";
import ActivityStatus from "./components/activity-status/activity-status";
import styles from "./detail.module.less";

const Detail: React.FC = () => {
  document.title = "品牌伯乐奖";
  const pageName = "品牌伯乐奖详情页";
  const track = Track();
  const { navigate2Product, navigate2Store } = useNavigation();
  const { shareAnnualBounsPosterAction } = useActions();
  const [search] = useSearchParams();
  const actId = search.get("actId") || "";

  const [pageInfo, setPageInfo] = useState<any>({});
  const [isExpand, setIsExpand] = useState(false);
  // 在 Detail 组件内部添加状态和逻辑
  const listType = useRef<"act" | "brand">("act");
  const firstRequestAct = useRef(true);
  const [activityListData, setActivityListData] = useState({
    hasNext: true,
    pageIndex: 1,
    pageSize: 10,
    pages: 0,
    totalCount: 0,
    result: [] as any[],
  });

  const [refreshing, setRefreshing] = useState(false);
  // 加载更多数据
  const loadMore = async () => {
    if (!activityListData.hasNext) {
      return;
    }

    try {
      if (listType.current === "act") {
        if (!pageInfo?.brandIdList || pageInfo?.brandIdList.length === 0) {
          listType.current = "brand";
          return loadMore();
        }
        const res = await queryActivityList({
          searchParams: {
            brandIds: pageInfo?.brandIdList,
            pageSize: activityListData.pageSize,
            pageNum: activityListData.pageIndex,
            isNeedSpuIds: true,
            activityStatus: 2,
            sortModel: 1,
            sortType: 2,
            scene: "INDEX",
          },
          voConfig: { showType: 2, withCardInsert: true, withProductCnt: 3, withProductCount: 3 },
          pageId: "H5_ScheduleList",
          storeIdsClick: "",
        });
        if ((!res || res.result?.length === 0) && firstRequestAct.current) {
          firstRequestAct.current = false;
          listType.current = "brand";
          return loadMore();
        }

        setActivityListData(prev => {
          const newResult = [...prev.result, ...(res?.result || [])];
          return {
            hasNext: res?.pageIndex ? res?.pageIndex < res?.pages : false,
            pageIndex: prev.pageIndex + 1,
            pageSize: prev.pageSize,
            pages: res?.pages || 0,
            totalCount: res?.totalCount || 0,
            result: newResult,
          };
        });
      } else if (listType.current === "brand") {
        if (!pageInfo?.merchantShopIdList || pageInfo?.merchantShopIdList.length === 0) {
          return setActivityListData(prev => {
            return {
              hasNext: false,
              pageIndex: prev.pageIndex,
              pageSize: prev.pageSize,
              pages: 0,
              totalCount: 0,
              result: [],
            };
          });
        }
        const res = await queryMerchantShopTabList({
          pageId: "H5_Shop",
          searchParams: {
            isNeedSpuIds: true,
            pageNum: activityListData.pageIndex,
            pageSize: activityListData.pageSize,
            merchantShopCodes: pageInfo?.merchantShopIdList,
          },
          voConfig: { withProduct: true, withProductCnt: 3 },
        });

        setActivityListData(prev => {
          const newResult = [...prev.result, ...(res?.result || [])];
          return {
            hasNext: res?.pageIndex ? res?.pageIndex < res?.pages : false,
            pageIndex: prev.pageIndex + 1,
            pageSize: prev.pageSize,
            pages: res?.pages || 0,
            totalCount: res?.totalCount || 0,
            result: newResult,
          };
        });
      }
    } catch (error) {
      console.error("加载数据失败:", error);
      return setActivityListData(prev => {
        return {
          hasNext: false,
          pageIndex: prev.pageIndex,
          pageSize: prev.pageSize,
          pages: prev.pages,
          totalCount: prev.totalCount,
          result: prev.result,
        };
      });
    }
  };

  // 下拉刷新
  const onRefresh = async () => {
    setRefreshing(true);
    try {
      if (listType.current === "act") {
        const res = await queryActivityList({
          searchParams: {
            brandIds: pageInfo?.brandIdList,
            pageSize: activityListData.pageSize,
            pageNum: 1,
            isNeedSpuIds: true,
            activityStatus: 2,
            sortModel: 1,
            sortType: 2,
            scene: "INDEX",
          },
          voConfig: { showType: 2, withCardInsert: true, withProductCnt: 3, withProductCount: 3 },
          pageId: "H5_ScheduleList",
          storeIdsClick: "",
        });

        setActivityListData(prev => {
          const newResult = [...(res?.result || [])];
          return {
            hasNext: res?.pageIndex ? res?.pageIndex < res?.pages : false,
            pageIndex: 1,
            pageSize: prev.pageSize,
            pages: res?.pages || 0,
            totalCount: res?.totalCount || 0,
            result: newResult,
          };
        });
      } else if (listType.current === "brand") {
        const res = await queryMerchantShopTabList({
          pageId: "H5_Shop",
          searchParams: {
            isNeedSpuIds: true,
            pageNum: 1,
            pageSize: activityListData.pageSize,
            merchantShopCodes: pageInfo?.merchantShopIdList,
          },
          voConfig: { withProduct: true, withProductCnt: 3 },
        });

        setActivityListData(prev => {
          const newResult = [...(res?.result || [])];
          return {
            hasNext: res?.pageIndex ? res?.pageIndex < res?.pages : false,
            pageIndex: 1,
            pageSize: prev.pageSize,
            pages: res?.pages || 0,
            totalCount: res?.totalCount || 0,
            result: newResult,
          };
        });
      }
    } catch (error) {
      console.error("刷新数据失败:", error);
    } finally {
      setRefreshing(false);
    }
  };

  const trackData = {
    page_name: pageName,
    previous_page_name: search.get("page_name") || "",
    resource_id: actId,
  };

  const onShare = async e => {
    e.stopPropagation();
    shareAnnualBounsPosterAction({
      contentId: pageInfo?.actCode,
      contentType: "ANNUAL_BONUS",
      pageName: "品牌伯乐奖",
      src: "AnnualBonusActivity",
    });
  };

  const footerDom = () => {
    if (["AWARD_CALCULATION", "AWARD_PROCESSING", "END"].includes(pageInfo?.taskStatus)) {
      // 活动已结束
      return <div className={`${styles.footer__btn} ${styles.disabled}`}>活动已结束</div>;
    } else {
      // 转发赚钱
      return (
        <div className={styles.footer__btn} onClick={e => onShare(e)}>
          <img
            className={styles.icon}
            src="https://akim-oss.aikucun.com/mshop/eeaaa563822b7c0c875f3c49e65c48e966db9716_1731416880122_13.png"
          />
          转发赚钱
        </div>
      );
    }
  };

  const getPageInfo = async () => {
    const data: any = await queryBonusByActId({
      actId,
    });

    setPageInfo(data);
  };

  useEffect(() => {
    track.pageView(pageName, {
      domain: "年终奖",
    });
    getPageInfo();
  }, [actId]);

  const {
    awardAmount,
    performanceAmount,
    performanceAmountUnit,
    currentAmount,
    currentAmountUnit,
    distanceNextStep,
    distanceNextStepUnit,
    isFinishMin,
    subTitle,
    ceilingCount,
    hasMoreNode,
    taskType,
  } = useMemo(() => {
    let isFinishMin = false;
    const awardAmount = (() => {
      const list = pageInfo?.userAwardInfo?.awardTaskList || [];
      for (let i = list.length - 1; i >= 0; i--) {
        if (list[i]?.reach) {
          isFinishMin = true;
          return list[i]?.awardAmount || "0";
        }
      }
      return "0";
    })();
    const isStandard = !!pageInfo?.isStandard;

    const awardInfos = Array.isArray(pageInfo?.awardInfos)
      ? pageInfo?.awardInfos.map((item, index) => {
          if (index === 0) {
            return [item.name, "奖励金额(元)"];
          }
          return [item.name, item.rewardAmount || "0"];
        })
      : [];

    const userAwardInfo = pageInfo?.userAwardInfo || {};

    const rank = userAwardInfo?.userRank || 0;

    const limitRank = userAwardInfo?.limitRank || 0;

    const inLimitRank = rank && limitRank && rank <= limitRank;

    const performanceAmount = pageInfo?.performanceAmount || "0";
    const performanceAmountUnit = pageInfo?.performanceAmountUnit;

    const expectAwardAmount = pageInfo?.expectAwardAmount || "0";

    const subTitle = userAwardInfo?.title || "";
    const currentAmount = userAwardInfo?.currentAmount || "0";
    const currentAmountUnit = userAwardInfo?.currentAmountUnit;
    const distanceNextStep =
      String(userAwardInfo?.distanceNextStep) === "-1"
        ? "0"
        : userAwardInfo?.distanceNextStep || "0";
    const distanceNextStepUnit = userAwardInfo?.distanceNextStepUnit;

    // -1 不可叠加  0 上不封顶  >0  封顶次数
    const ceilingCount = pageInfo?.ceilingCount || 0;

    // 是否有更多节点
    const hasMoreNode = !!pageInfo?.userAwardInfo?.hasMoreNode;

    const taskType = String(userAwardInfo?.taskType || "");
    return {
      isFinishMin,
      awardAmount,
      performanceAmount,
      expectAwardAmount,
      performanceAmountUnit,
      subTitle,
      currentAmount,
      currentAmountUnit,
      distanceNextStep,
      distanceNextStepUnit,
      ceilingCount,
      hasMoreNode,
      taskType,
    };
  }, [pageInfo]);

  const nextStepDesc = useMemo(() => {
    const userAwardInfo = pageInfo?.userAwardInfo || {};
    if (pageInfo && ceilingCount === 0) {
      return <div className={styles.nextStepDesc}>奖励上不封顶</div>;
    }
    if (!userAwardInfo.nextStepAward || userAwardInfo.nextStepAward === -1) {
      return null;
    }
    if (ceilingCount === -1) {
      return (
        <div className={styles.nextStepDesc}>
          业绩再提升<span className={styles.highlight}>{userAwardInfo.distanceNextStep}</span>
          {userAwardInfo.distanceNextStepUnit}，可获得
          <span className={styles.highlight}>{userAwardInfo.nextStepAward}</span>元奖励
        </div>
      );
    }
    return (
      <div className={styles.nextStepDesc}>
        最高封顶<span className={styles.highlight}>{userAwardInfo.distanceNextStep}</span>
        {userAwardInfo.distanceNextStepUnit}，最多可获得
        <span className={styles.highlight}>{userAwardInfo.nextStepAward}</span>元奖励
      </div>
    );
  }, [pageInfo]);

  const popInfo = useMemo(() => {
    return (
      pageInfo?.userAwardInfo?.awardTaskList?.map(item => {
        return {
          topText: item.award,
          bottomText: item.target,
          reach: item.reach,
        };
      }) || []
    );
  }, [pageInfo]);

  const subProcessItemProps = useMemo(() => {
    if (taskType === "1") {
      return undefined;
    }
    return {
      width: 328,
      label: subTitle,
      subLabel: `（当前${currentAmount}${currentAmountUnit}）`,
      desc: nextStepDesc,
      tip: "参与结对品牌在各渠道的年度累计总业绩目标达成情况",
      topText: "奖励",
      bottomText: "目标",
      popInfo,
      valuePopBg:
        "https://akim-oss.aikucun.com/04f0f71b20752a443bc0cb6f115269e8688be432_1747830340477_55.png",
      processBarBg: "#DAEEFF",
      percentBg:
        "https://akim-oss.aikucun.com/9803593f41a4cbb6f5a486c81d19c88c90a107bf_1747915853005_9.png",
      targetBg:
        "https://akim-oss.aikucun.com/ffc5323c86e8eb6636391a063ed6cdec513b97dc_1747830385727_70.png",
      percentType: (popInfo.length < 3
        ? "line"
        : ceilingCount === -1 || popInfo.filter(item => item.reach).length >= 2
          ? "line"
          : "point") as any,
      hasMore: popInfo.length < 3 ? false : hasMoreNode,
      isHighLightLastPop: popInfo.length > 0 && ceilingCount === 0,
    };
  }, [pageInfo]);

  const onShopAction = (e, index?: any) => {
    console.log("点击店铺回调", e);
    if (index >= 0) {
      track.track("resource_click", {
        page_name: "店铺聚合",
        previous_page_name: search.get("page_name"),
        domain: "B2R旗舰店",
        module: "店铺列表",
        resource_content: e?.shopName,
        resource_rank: index,
        merchant_shop_code: e?.shopCode,
      });
    } else {
      track.track("resource_click", {
        page_name: "店铺聚合",
        previous_page_name: search.get("page_name"),
        domain: "B2R旗舰店",
        resource_content: "B2R旗舰店",
        module: "关注好店",
        merchant_shop_code: e?.shopCode,
        btn_name: e?.shopName,
      });
    }
    navigate2Store({
      merchantShopCode: e?.shopCode,
      shopCode: e?.shopCode,
      page_name: encodeURIComponent("店铺聚合"),
      productType: 1,
    });
  };
  const onProductAction = (e, index) => {
    console.log("点击商品回调", e);
    track.track("resource_click", {
      page_name: "店铺聚合",
      previous_page_name: search.get("page_name"),
      domain: "B2R旗舰店",
      module: "店铺列表",
      activity_spu_code: e.activitySpuId,
      activity_id: e.activityId,
      sku_id: e.selectSkuId,
      btn_name: "商品点击",
      resource_rank: index,
      merchant_shop_code: e.shopCode,
    });
    navigate2Product({
      activityId: e.activityId,
      activitySpuId: e.activitySpuId,
      selectSkuId: e.selectSkuId,
    });
  };

  return (
    <div className={styles.page}>
      <div className={styles.scroll_view}>
        <div className={styles.scroll__content}>
          <div className={styles.page__header}>
            <div className={styles["activity-name-wrap"]}>
              <h3 className={`${styles["activity_name"]}`}>{pageInfo.name}</h3>
            </div>
            {pageInfo.actDesc && (
              <p className={`${styles["activity_desc"]} ${styles["text-ellipsis1"]}`}>
                {pageInfo.actDesc}
              </p>
            )}
            {pageInfo?.status && (
              <ActivityStatus
                sysTime={pageInfo.sysTime}
                taskEndTime={pageInfo?.endTime}
                taskStatus={pageInfo?.status}
                backgroundColor="radial-gradient(12% 88% at 92% 50%, #1B92FE 0%, #0A55FC 100%)"
                borderColor="rgba(255, 255, 255, 0.5)"
                textBackgroundColor="#0B3E96"
              />
            )}
          </div>
          {/* 规则控件 */}
          <RulePageIcon url={pageInfo?.planRule} top={126} />
          <div className={styles.pageContent}>
            {/* 总览区域 */}
            {pageInfo?.status === "BEGIN" && (
              <OverviewPane
                items={[
                  {
                    value: awardAmount,
                    unit: "元",
                    label: "奖池总金额",
                    tip: "截止当前日期该活动总业绩对应的奖池总额（非可瓜分奖池总额），最终可瓜分奖池总额按活动结束后渠道业绩占比确定",
                    valueColor: "#333333",
                  },
                  {
                    value: performanceAmount,
                    unit: performanceAmountUnit,
                    label: "当前品牌业绩",
                    valueColor: "#333333",
                  },
                  {
                    value: distanceNextStep,
                    unit: distanceNextStepUnit,
                    label: "奖励升级业绩差额",
                    valueColor: "#333333",
                  },
                ]}
              />
            )}
            {pageInfo?.status !== "BEGIN" && (
              <div className={styles.endOverviewPane}>
                {isFinishMin ? (
                  <>
                    <div className={styles.endOverviewPane__title}>
                      恭喜获得
                      <span className={styles.endOverviewPane__title__amount}>{awardAmount}</span>
                      元活动奖励瓜分资格
                    </div>
                    <div className={styles.endOverviewPane__tip}>
                      您的奖励金额将按活动结束时渠道业绩占比确定
                    </div>
                  </>
                ) : (
                  <div className={styles.endOverviewPane__tip2}>很遗憾，您未达到活动获奖门槛</div>
                )}
              </div>
            )}
            {/* 进度详情 */}
            {subProcessItemProps && <SubProcessItem {...subProcessItemProps} />}
            <div className={styles.tipWrapper}>
              <div className={styles.labelWrapper}>
                <span className={styles.label}>奖励规则</span>
              </div>
              <div className={styles.tipRuleWrapper}>
                <p className={styles.tipRule}>1、本活动定向邀请参与，参与店主仅可见结对品牌；</p>
                <p className={styles.tipRule}>
                  2、参与店主对该品牌的总业绩负责，并按总业绩达成和渠道占比均分奖励；
                </p>
                <p className={styles.tipRule}>
                  3、本活动数据按T+1更新，奖池金额也将按达成进度在日次更新；
                </p>
                {isExpand && (
                  <>
                    <p className={styles.tipRule}>
                      4、本活动按活动时间内的实际支付销售额进行统计，经系统判定为风险订单、取消以及退货退款等情况则不纳入活动统计范畴；
                    </p>
                    <p className={styles.tipRule}>
                      5、活动奖励将于26年2月以饷店余额的形式发放，其他详细规则可在&ldquo;规则&rdquo;页中查看。
                    </p>
                  </>
                )}
                <div onClick={() => setIsExpand(!isExpand)} className={styles.expandBtn}>
                  <span className={styles.expandBtn__text}>{isExpand ? "收起" : "展开"}</span>
                  {isExpand ? <UpOutline /> : <DownOutline />}
                </div>
              </div>
            </div>
          </div>
          {/* 会场列表 */}
          {!!pageInfo && Object.keys(pageInfo) && (
            <div className={styles.brandListWrapper}>
              <PullToRefresh onRefresh={onRefresh}>
                <div className={styles.activityList}>
                  {activityListData.result.map((item, index) => (
                    <div key={index} className={styles.activityItem}>
                      {listType.current === "act" ? (
                        <ActivityCardNormalV2Comp
                          model={item}
                          trackData={{
                            domain: "品牌伯乐奖",
                          }}
                          mainLocationCode="H5_ScheduleList_BrandDown"
                          productTLLocationCode="H5_ScheduleList_TopProductPicLeft"
                          productTRLocationCode="H5_ScheduleList_TopProductPicRight"
                        />
                      ) : (
                        <ShopCardV2
                          nameFrontTagLocationCode={`${hostEnv}_Shop_ShopNameFront`}
                          mainLocationCode={`${hostEnv}_Shop_ShopNameDown`}
                          shopInfo={item}
                          index={index}
                          sign="shop-list"
                          onShopAction={(e: any) => onShopAction(e, index)}
                          onProductAction={e => onProductAction(e, index)}
                          labelLocationVOList={item?.labelLocationVOList}
                          profitTag="赚"
                          role={getUserRole()}
                          terminal="h5"></ShopCardV2>
                      )}
                    </div>
                  ))}
                  <InfiniteScroll
                    loadMore={loadMore}
                    hasMore={activityListData.hasNext}
                    threshold={250}>
                    {""}
                  </InfiniteScroll>
                </div>
              </PullToRefresh>
            </div>
          )}
        </div>
      </div>

      {/* footer */}
      <div className={styles.page__footer}>{footerDom()}</div>
    </div>
  );
};
export default Detail;
